import { effect, inject, Injectable, Injector, computed, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {applyEach, form, max, min, minLength, pattern, required} from '@angular/forms/signals';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, of, take, tap } from 'rxjs';
import { HttpService } from '@core/services/http.service';
import { DialogConfig, DialogService } from '@shared/components/confirmation-dialog/confirmation-dialog';
import { AssetsStatusService } from '@app/features/assets-status/core/services/assets-status.service';
import { FormattedLandDataModel } from '@app/features/land-form/core/models/formatted-land-data.model';
import {
  createDefaultCoordinates,
  createEmptyLandFormModel,
  FormMode,
  GeoCoordinateModel,
  LandFormModel,
} from '@app/features/land-form/core/models/land-form.model';
import { LandAssetModel } from '@app/features/land-form/core/models/land-asset.model';
import {AppRoutes} from '@core/constant/app-routes.constant';

const NAME_EN_PATTERN = /^[a-zA-Z0-9\s_\-]+$/;
const NAME_AR_PATTERN = /^[\u0600-\u06FF0-9\s_\-]+$/;

@Injectable()
export class LandFormPageService {
  private readonly assetsService = inject(AssetsStatusService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly httpService = inject(HttpService);
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);
  public readonly appRoutes = AppRoutes;
  readonly mode = signal<FormMode>('add');
  readonly landId = signal<string | null>(null);
  readonly currentTab = signal(0);
  readonly isLoading = signal(false);
  readonly loading = signal(false);
  readonly isSubmitting = signal(false);
  readonly isDataLoaded = signal(false);
  readonly formattedData = signal<FormattedLandDataModel | null>(null);

  readonly regions = signal<any[]>([]);
  readonly cities = signal<any[]>([]);
  readonly districts = signal<any[]>([]);
  readonly categories = signal<any[]>([]);

  readonly tabs = signal([
    { id: 0, name: 'landForm.tabs.landAssets', active: true },
    { id: 1, name: 'landForm.tabs.siteDetails', active: false },
    { id: 2, name: 'landForm.tabs.demandDrivers', active: false },
    { id: 3, name: 'landForm.tabs.growthTrajectory', active: false },
    { id: 4, name: 'landForm.tabs.regulatoryInfrastructure', active: false },
  ]);

  readonly landModel = signal<LandFormModel>(createEmptyLandFormModel());

  readonly landForm = form(
    this.landModel,
    (path) => {
      required(path.name, { message: 'this field is required' });
      minLength(path.name, 2);
      pattern(path.name, NAME_EN_PATTERN, { message: 'englishOnly' });

      required(path.name_ar, { message: 'this field is required' });
      minLength(path.name_ar, 2);
      pattern(path.name_ar, NAME_AR_PATTERN, { message: 'arabicOnly' });

      required(path.region, { message: 'this field is required' });
      required(path.real_estate_type_categories_id, { message: 'this field is required' });
      required(path.city_id, { message: 'this field is required' });
      required(path.district_id, { message: 'this field is required' });

      required(path.area, { message: 'this field is required' });
      min(path.area, 0, { message: 'minValue' });

      required(path.reference, { message: 'this field is required' });
      required(path.lat,{ message: 'this field is required' })
      required(path.lng,{ message: 'this field is required' })

      required(path.land_purchase, { message: 'this field is required' });
      min(path.land_purchase, 0, { message: 'minValue' });
      applyEach(path.geoFenceCoordinates, (coord) => {
        required(coord.latitude, { message: 'this field is required' });
        required(coord.longitude, { message: 'this field is required' });
      });
    },
    { injector: this.injector }
  );

  readonly currentDialogConfig = toSignal(this.dialogService.dialogConfig, {
    initialValue: null as DialogConfig | null,
  });
  readonly isDialogVisible = toSignal(this.dialogService.isVisible, {
    initialValue: false,
  });

  readonly isAddMode = computed(() => this.mode() === 'add');
  readonly isEditMode = computed(() => this.mode() === 'edit');
  readonly isViewMode = computed(() => this.mode() === 'view');

  constructor() {
    // ✅ Effect لمتابعة التغييرات في assetTypeDetail
    effect(() => {
      const assetsType = this.assetsService.assetTypeDetail();
      if (!assetsType) {
        return;
      }

      untracked(() => {
        this.landModel.update((model) => {
          const detail = { ...model.asset_type_detail };
          
          // ✅ دمج القيم الجديدة مع القديمة
          Object.keys(assetsType).forEach((key) => {
            if (assetsType[key] !== null && assetsType[key] !== undefined) {
              detail[key] = assetsType[key];
            }
          });
          
          // ✅ تأكد من وجود real_estate_type_id
          if (assetsType['real_estate_type_id'] && !detail['real_estate_type_id']) {
            detail['real_estate_type_id'] = assetsType['real_estate_type_id'];
          }
          
          return { ...model, asset_type_detail: detail };
        });
      });
    });

    effect(() => {
      const model = this.landModel();
      if (!this.isDataLoaded()) {
        return;
      }

      untracked(() => {
        const hasContent = model.name || model.area;
        if (hasContent) {
          this.assetsService.landFormStorage.set(model);
          sessionStorage.setItem('landForm', JSON.stringify(model));
        }
      });
    });

    this.initializeFromRoute();
  }

  private initializeFromRoute(): void {
    const routeId = this.route.snapshot.paramMap.get('id');
    const queryParams = this.route.snapshot.queryParamMap;
    const queryMode = queryParams.get('mode') as FormMode | null;
    const queryId = queryParams.get('id');

    if (routeId) {
      this.mode.set('view');
      this.landId.set(routeId);
    } else if (queryMode === 'view' && queryId) {
      void this.router.navigate(['/land', queryId], { replaceUrl: true });
      return;
    } else {
      this.mode.set(queryMode || 'add');
      this.landId.set(queryId);
    }

    this.loadCategories().then(() => {
      this.loadRegions().subscribe(() => this.initializeForm());
    });
  }

  canNavigateToTab(tabIndex: number): boolean {
    if (this.mode() === 'add' || this.mode() === 'edit') {
      return tabIndex === 0;
    }
    return true;
  }

  goToTab(index: number): void {
    const tabs = this.tabs();
    if (index < 0 || index >= tabs.length || index === this.currentTab()) {
      return;
    }
    if (!this.canNavigateToTab(index)) {
      return;
    }

    this.currentTab.set(index);
    this.tabs.set(tabs.map((tab, i) => ({ ...tab, active: i === index })));

    queueMicrotask(() => {
      document.querySelector('.scrollable-content')?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  onRegionChange(selectedRegion: any): void {
    if (selectedRegion?.id) {
      this.landModel.update((m) => ({ ...m, region: selectedRegion.id, city_id: null, district_id: null }));
      this.loadCities(selectedRegion.id);
    }
  }

  onCityChange(selectedCity: any): void {
    if (selectedCity?.id) {
      this.landModel.update((m) => ({ ...m, city_id: selectedCity.id, district_id: null }));
      this.loadDistricts(selectedCity.id);
    }
  }

  onDistrictChange(selectedDistrict: any): void {
    if (selectedDistrict?.id) {
      this.landModel.update((m) => ({ ...m, district_id: selectedDistrict.id }));
    }
  }

  onCategoryChange(event: any): void {
    if (event?.have_drop !== 1) {
      return;
    }

    sessionStorage.setItem('category-name', event?.name);
    sessionStorage.setItem('real_estate_type_categories_id', event?.id);

    this.landModel.update((m) => ({ ...m, real_estate_type_categories_id: event?.id }));

    const landId = this.landId();
    this.router.navigate([this.appRoutes.landForm.details(this.mode(),landId ? landId : '0' ,event.id)]);
  }

  addCoordinate(): void {
    this.landModel.update((m) => ({
      ...m,
      geoFenceCoordinates: [...m.geoFenceCoordinates, { latitude: '', longitude: '' }],
    }));
  }

  removeCoordinate(index: number): void {
    this.landModel.update((m) => {
      if (m.geoFenceCoordinates.length <= 3) {
        return m;
      }
      return {
        ...m,
        geoFenceCoordinates: m.geoFenceCoordinates.filter((_, i) => i !== index),
      };
    });
  }

  fieldHasError(field: keyof LandFormModel, errorKind?: string): boolean {
    if (!this.isSubmitting()) {
      return false;
    }

    const fieldState = (this.landForm as any)[field]?.();
    if (!fieldState?.touched?.()) {
      return false;
    }

    if (errorKind) {
      return fieldState.errors().some((e: { kind: string }) => e.kind === errorKind);
    }

    return fieldState.invalid();
  }

  onSave(): void {
    this.isSubmitting.set(true);

    this.loading.set(true);

    const formValue = this.landModel();
    const coordinates = formValue.geoFenceCoordinates.map(
      (p) => [Number(p.longitude), Number(p.latitude)] as [number, number]
    );

    if (coordinates.length > 0) {
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];
      if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        coordinates.push(firstPoint);
      }
    }

    // ✅ استخدم asset_type_detail كاملاً
    const assetTypeDetail = { ...formValue.asset_type_detail };
    
    // ✅ خذ real_estate_type_id من asset_type_detail
    let realEstateTypeId = assetTypeDetail['real_estate_type_id'];
    
    // ✅ لو مش موجود، جيبها من formValue
    if (!realEstateTypeId) {
      realEstateTypeId = (formValue as any).real_estate_type_id;
    }

    const payload: any = {
      ...formValue,
      region_id: formValue.region,
      real_estate_type_id: realEstateTypeId,
      geofence: { rings: [coordinates] },
      asset_type_detail: assetTypeDetail,
    };

    delete (payload as any).region;
    delete (payload as any).geoFenceCoordinates;

    const mode = this.mode();
    const landId = this.landId();

    if (mode === 'add') {
      this.createLandAsset(payload).pipe(take(1)).subscribe({
        next: () => this.onSaveSuccess(),
        error: (error) => this.onSaveError(error),
      });
    } else if (mode === 'edit' && landId) {
      this.updateLandAsset(landId, payload).pipe(take(1)).subscribe({
        next: () => {
          this.assetsService.assetTypeDetail.set(null);
          this.assetsService.landFormStorage.set(null);
          this.onSaveSuccess();
        },
        error: (error) => this.onSaveError(error),
      });
    }
  }

  onCancel(): void {
    sessionStorage.removeItem('real_estate_type_categories_id');
    sessionStorage.removeItem('landForm');
    this.router.navigate(['/land-assets']);
    this.assetsService.assetTypeDetail.set(null);
    this.assetsService.landFormStorage.set(null);
  }

  onDialogAction(): void {
    this.dialogService.hideDialog();
  }

  private initializeForm(): void {
    const mode = this.mode();

    if (mode === 'add') {
      this.resetFormForAdd();
      return;
    }

    if (mode === 'view' || mode === 'edit') {
      const id = this.landId();
      if (!id) {
        return;
      }
      this.loadExistingLand(id);
    }
  }

  private resetFormForAdd(): void {
    this.landModel.set({
      ...createEmptyLandFormModel(),
      geoFenceCoordinates: createDefaultCoordinates(),
    });
    this.isDataLoaded.set(false);

    const storageData = sessionStorage.getItem('landForm');
    const savedForm = this.assetsService.landFormStorage() || (storageData ? JSON.parse(storageData) : null);
    const newDetails = this.assetsService.assetTypeDetail();
    const savedCategoryId = sessionStorage.getItem('real_estate_type_categories_id');

    if (savedForm || newDetails) {
      const mergedData = {
        ...savedForm,
        asset_type_detail: {
          ...(savedForm?.asset_type_detail || {}),
          ...(newDetails || {}),
        },
      };

      if (savedCategoryId && savedForm && !savedForm.real_estate_type_categories_id) {
        mergedData.real_estate_type_categories_id = parseInt(savedCategoryId, 10);
      }

      this.applyStoredData(mergedData);
    } else if (savedCategoryId) {
      this.landModel.update((m) => ({
        ...m,
        real_estate_type_categories_id: parseInt(savedCategoryId, 10),
      }));
      this.isDataLoaded.set(true);
    } else {
      this.isDataLoaded.set(true);
    }
  }

  private async applyStoredData(mergedData: any): Promise<void> {
    if (this.mode() !== 'add') {
      return;
    }
  
    if (mergedData.region) {
      await this.loadCities(mergedData.region);
    }
  
    if (mergedData.city_id && this.cities().length > 0) {
      await this.loadDistricts(mergedData.city_id);
    }
  
    const { asset_type_detail, geoFenceCoordinates, ...restData } = mergedData;
  
    const coords =
      geoFenceCoordinates && geoFenceCoordinates.length > 0
        ? geoFenceCoordinates
        : createDefaultCoordinates();
  
    let categoryId = mergedData.real_estate_type_categories_id;
    if (!categoryId) {
      const savedId = sessionStorage.getItem('real_estate_type_categories_id');
      if (savedId) {
        categoryId = parseInt(savedId, 10);
      }
    }
  
    // ✅ تأكد من وجود البيانات في asset_type_detail
    const details = { ...(asset_type_detail || {}) };
    
    // ✅ لو في real_estate_type_id في restData، ضيفها للـ details
    if (!details['real_estate_type_id'] && restData['real_estate_type_id']) {
      details['real_estate_type_id'] = restData['real_estate_type_id'];
    }
  
    // ✅ لو في real_estate_type_id في mergedData، ضيفها للـ details
    if (!details['real_estate_type_id'] && mergedData.real_estate_type_id) {
      details['real_estate_type_id'] = mergedData.real_estate_type_id;
    }
  
    console.log('✅ applyStoredData - details:', details);
  
    this.landModel.set({
      ...createEmptyLandFormModel(),
      ...restData,
      asset_type_detail: details,
      geoFenceCoordinates: coords,
      real_estate_type_categories_id: categoryId ?? null,
    } as LandFormModel);
  
    this.isDataLoaded.set(true);
  }

  private loadExistingLand(id: string): void {
    this.isLoading.set(true);
  
    this.loadLandData(id)
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (data: any) => {
          Promise.all([
            this.loadCities(data?.data?.region_id),
            this.loadDistricts(data?.data?.city_id),
          ]).then(() => {
            const savedCategoryId = sessionStorage.getItem('real_estate_type_categories_id');
            const categoryId = savedCategoryId || data.data.real_estate_type_categories_id;
            const category = this.categories().find((c: any) => c.id == categoryId);
            if (category?.name) {
              sessionStorage.setItem('category-name', category.name);
            }
  
            let coordinates: GeoCoordinateModel[] = [];
            if (data.data.geofence?.rings?.[0]) {
              coordinates = data.data.geofence.rings[0].map(([lng, lat]: [number, number]) => ({
                latitude: lat,
                longitude: lng,
              }));
            } else if (data.data.geoFenceCoordinates?.length > 0) {
              coordinates = data.data.geoFenceCoordinates;
            }
  
            // ✅ استخدم asset_type_details (مع s)
            const assetTypeDetail = { ...(data.data.asset_type_details || {}) };
            
            // ✅ real_estate_type_id بره asset_type_details
            // خزنه في الـ model عشان الـ p-select ياخده
            const realEstateTypeId = data.data.real_estate_type_id || null;
  
            console.log('✅ loadExistingLand - assetTypeDetail:', assetTypeDetail);
            console.log('✅ loadExistingLand - real_estate_type_id:', realEstateTypeId);
  
            const mappedData: any = {
              name: data.data.name,
              name_ar: data.data.name_ar,
              region: data.data.region_id,
              real_estate_type_categories_id: savedCategoryId
                ? parseInt(savedCategoryId, 10)
                : data.data.real_estate_type_categories_id,
              district_id: data.data.district_id,
              city_id: data.data.city_id,
              land_purchase: data.data.land_purchase,
              area: data.data.area,
              reference: data.data.reference ?? '',
              lat: data.data.lat ?? '',
              lng: data.data.lng ?? '',
              geoFenceCoordinates: coordinates.length ? coordinates : createDefaultCoordinates(),
              asset_type_detail: assetTypeDetail,
              real_estate_type_id: realEstateTypeId, // ✅ خزنها في الـ model
            };
  
            this.landModel.set(mappedData as LandFormModel);
            sessionStorage.setItem('landForm', JSON.stringify(mappedData));
            this.isDataLoaded.set(true);
  
            console.log('✅ Final Model:', this.landModel());
          });
        },
        error: () => this.isDataLoaded.set(true),
      });
  
    this.loadFormattedData(id).pipe(take(1)).subscribe({
      next: (data: any) => this.formattedData.set(data.data),
    });
  }
  // private loadExistingLand(id: string): void {
  //   this.isLoading.set(true);
  
  //   this.loadLandData(id)
  //     .pipe(
  //       take(1),
  //       finalize(() => this.isLoading.set(false))
  //     )
  //     .subscribe({
  //       next: (data: any) => {
  //         Promise.all([
  //           this.loadCities(data?.data?.region_id),
  //           this.loadDistricts(data?.data?.city_id),
  //         ]).then(() => {
  //           const savedCategoryId = sessionStorage.getItem('real_estate_type_categories_id');
  //           const categoryId = savedCategoryId || data.data.real_estate_type_categories_id;
  //           const category = this.categories().find((c: any) => c.id == categoryId);
  //           if (category?.name) {
  //             sessionStorage.setItem('category-name', category.name);
  //           }
  
  //           let coordinates: GeoCoordinateModel[] = [];
  //           if (data.data.geofence?.rings?.[0]) {
  //             coordinates = data.data.geofence.rings[0].map(([lng, lat]: [number, number]) => ({
  //               latitude: lat,
  //               longitude: lng,
  //             }));
  //           } else if (data.data.geoFenceCoordinates?.length > 0) {
  //             coordinates = data.data.geoFenceCoordinates;
  //           }
  
  //           // ✅ استخدم asset_type_details (مع s) مش asset_type_detail
  //           const assetTypeDetail = { ...(data.data.asset_type_details || {}) };
            
  //           // ✅ لو real_estate_type_id موجودة في الـ data, ضيفها للـ asset_type_detail
  //           if (data.data.real_estate_type_id && !assetTypeDetail['real_estate_type_id']) {
  //             assetTypeDetail['real_estate_type_id'] = data.data.real_estate_type_id;
  //           }
  
  //           console.log('✅ loadExistingLand - assetTypeDetail:', assetTypeDetail);
  //           console.log('✅ loadExistingLand - data.data.real_estate_type_id:', data.data.real_estate_type_id);
  
  //           const mappedData: any = {
  //             name: data.data.name,
  //             name_ar: data.data.name_ar,
  //             region: data.data.region_id,
  //             real_estate_type_categories_id: savedCategoryId
  //               ? parseInt(savedCategoryId, 10)
  //               : data.data.real_estate_type_categories_id,
  //             district_id: data.data.district_id,
  //             city_id: data.data.city_id,
  //             land_purchase: data.data.land_purchase,
  //             area: data.data.area,
  //             reference: data.data.reference ?? '',
  //             lat: data.data.lat ?? '',
  //             lng: data.data.lng ?? '',
  //             geoFenceCoordinates: coordinates.length ? coordinates : createDefaultCoordinates(),
  //             asset_type_detail: assetTypeDetail, // ✅ استخدم asset_type_details
  //             real_estate_type_id: data.data.real_estate_type_id,
  //           };
  
  //           this.landModel.set(mappedData as LandFormModel);
  //           sessionStorage.setItem('landForm', JSON.stringify(mappedData));
  //           this.isDataLoaded.set(true);
  
  //           console.log('✅ Final Model:', this.landModel());
  //         });
  //       },
  //       error: () => this.isDataLoaded.set(true),
  //     });
  
  //   this.loadFormattedData(id).pipe(take(1)).subscribe({
  //     next: (data: any) => this.formattedData.set(data.data),
  //   });
  // }

  private loadCategories(): Promise<boolean> {
    return new Promise((resolve) => {
      this.assetsService.getRealStateTypeCategory().pipe(take(1)).subscribe({
        next: (res: any) => {
          this.categories.set(res?.success ? res.data : []);
          resolve(true);
        },
        error: () => {
          this.categories.set([]);
          resolve(true);
        },
      });
    });
  }

  private loadRegions() {
    return this.httpService.get('admin/chart-parameters/regions').pipe(
      tap((res: any) => {
        if (res.success) {
          this.regions.set(res.data);
        }
      }),
      catchError(() => of(null))
    );
  }

  private loadCities(regionId: number): Promise<boolean> {
    return new Promise((resolve) => {
      this.assetsService.getCities(regionId).pipe(take(1)).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.cities.set(res.data);
          }
          resolve(true);
        },
        error: () => resolve(true),
      });
    });
  }

  private loadDistricts(cityId: number): Promise<boolean> {
    return new Promise((resolve) => {
      this.assetsService.getDistricts(cityId).pipe(take(1)).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.districts.set(res.data);
          }
          resolve(true);
        },
        error: () => resolve(true),
      });
    });
  }

  private loadLandData(id: string) {
    return this.httpService.get<LandAssetModel>(`admin/lands/${id}`);
  }

  private loadFormattedData(id: string) {
    return this.httpService.get<{ data: FormattedLandDataModel }>(`admin/lands/${id}/formatted`);
  }

  private createLandAsset(data: unknown) {
    return this.httpService.post('admin/lands', data);
  }

  private updateLandAsset(id: string, data: unknown) {
    return this.httpService.put(`admin/lands/${id}`, data);
  }

  private onSaveSuccess(): void {
    this.isSubmitting.set(false);
    sessionStorage.removeItem('real_estate_type_categories_id');
    sessionStorage.removeItem('landForm');
    this.loading.set(false);
    this.router.navigate([this.appRoutes.home]);
  }

  private onSaveError(error: any): void {
    this.isSubmitting.set(false);
    this.loading.set(false);
    this.handleApiError(error);
  }

  private handleApiError(error: any): void {
    let subtitle = this.translateService.instant('landForm.errors.pleaseTryAgain');

    if (error?.error?.errors) {
      subtitle = Object.values(error.error.errors).flat().join(' | ');
    } else if (error?.error?.message) {
      subtitle = error.error.message;
    }

    this.dialogService.showDialog({
      type: 'error',
      title: this.translateService.instant('landForm.errors.somethingWentWrong'),
      subtitle,
      showFooter: false,
    });
  }

  private markAllFieldsTouched(): void {
    const fields: (keyof LandFormModel)[] = [
      'name', 'name_ar', 'region', 'city_id', 'district_id',
      'real_estate_type_categories_id', 'area', 'reference',
      'land_purchase', 'lat', 'lng',
    ];

    fields.forEach((field) => {
      (this.landForm as any)[field]?.().markAsTouched?.();
    });
  }

  private emptySiteInformation() {
    return {
      area_id: null, shape_id: null, flood_risk_id: null, floodplain: null,
      topography_id: null, buildable_area_percent: null, soil_condition_id: null,
      soil_bearing_capacity: null, requires_piling: null, floor_area_ratio_max: null,
      soil_foundation_required_id: null, land_use_flexibility_score: null,
      proximity_to_major_roads_id: null, public_transportation_access_id: null,
      traffic_pattern_id: null, visibility_and_access_point_id: null,
      proximity_to_urban_center_id: null,
    };
  }

  private emptyMarketDemographics() {
    return {
      population_density_id: null, income_level_id: null, age_distribution_id: null,
      current_land_trends_id: null, market_analysis_id: null, competitor_analysis_id: null,
      land_valuation_id: null, potential_roi_id: null, development_costs_id: null,
      financing_options_id: null, price_per_sqm: null, target_use_case: null,
      market_demand_score: null, competitive_supply_index: null, eligible_financing: null,
      financing_sources: null, loan_to_value_ratio: null, projected_roi_percent: null,
      revenue_sqm_forecast: null, roi_model_certainty: null, is_in_growth_zone: null,
      growth_certainty_id: null, existing_communities_id: null, public_sentiment_id: null,
      amenity_score: null, cultural_heritage_considerations_id: null, heritage_nearby: null,
      cultural_flag: null, cultural_risk_level_id: null, access_amenities_id: null,
      distance_school: null, distance_health: null, distance_transport: null,
      climatic_conditions_id: null, environmental_constraints_id: null, environmental_score: null,
      certification_type_id: null, sustainability_standards_id: null,
      waste_management_strategies_id: null, energy_efficiency_opportunities_id: null,
      impact_biodiversity_id: null,
    };
  }

  private emptyGrowthPlanning() {
    return {
      landlocked_parcels_id: null, views_and_vistas_id: null,
      right_of_way_or_easements_id: null, easement_area_percent: null,
      potential_for_assemblage_id: null, unique_natural_features_id: null,
      urban_growth_trends_id: null, government_development_plans_id: null,
      smart_infra_type_id: null, included_in_master_plan: null, master_plan_zone_type_id: null,
      planned_infrastructure_projects_id: null, technology_integration_id: null,
      distance_to_smart_city_node: null,
    };
  }

  private emptyLegalInfrastructure() {
    return {
      land_ownership_title_id: null, legal_title_status_id: null,
      development_incentives_restrictions_id: null, building_codes_id: null,
      zoning_regulations_id: null, zoning_flexibility_index: null,
      environmental_regulations_id: null, historical_cultural_designations_id: null,
      height_restrictions_id: null, road_network_id: null, water_supply_id: null,
      road_type_id: null, road_condition_score: null, sewage_waste_management_id: null,
      telecommunications_infrastructure_id: null, electricity_grids_id: null,
      stormwater_drainage_systems_id: null, distance_utilities: null, infrastructure_score: null,
    };
  }
}